import _ from 'lodash';
import {
  AdminBundle,
  VariantOverview,
  AdminCategory,
  AdminBundleItem,
  LocaleResponse,
  BundleImage,
  AdminProductVariant,
  SalesChannel,
  Country,
  BundlePrice,
} from '@cdaas/api-clients/dist/product-service-openapi';

import { AdminBundleItemWithKey } from '../../../utils/types';

export interface VariantEverything extends VariantOverview, AdminProductVariant {}

export function isVariantEverything(variant: VariantEverything | VariantOverview): variant is VariantEverything {
  return (variant as VariantEverything).finance_product_ids !== undefined;
}

export interface BundleEditProps {
  id: number | undefined;
  name: string | undefined;
  handleNameChange: (name: string) => void;
  nameTranslations: { localeId: number; translation: string }[] | undefined;
  handleNameTranslationsChange: (translations: { localeId: number; translation: string }[]) => void;
  financeId: string | undefined;
  handleFinanceIdChange: (financeId: string) => void;
  description: string | undefined;
  handleDescriptionChange: (description: string) => void;
  descriptionTranslations: { localeId: number; translation: string }[] | undefined;
  handleDescriptionTranslationsChange: (translations: { localeId: number; translation: string }[]) => void;
  translationsMetadata: { id: number; localeId: number }[] | undefined;
  images: BundleImage[] | undefined;
  handleImagesChange: (images: BundleImage[]) => void;

  mainItemOptionIds: number[] | undefined;
  handleMainItemOptionIdsChange: (optionIds: number[]) => void;
  items: AdminBundleItem[] | undefined;
  handleItemsChange: (items: AdminBundleItem[]) => void;
  variants: VariantEverything[] | undefined;
  handleVariantsChange: (variants: (VariantOverview | VariantEverything)[]) => Promise<boolean>;

  allCategoriesInSystem: AdminCategory[];
  allSalesChannelsInSystem: SalesChannel[];
  allCountriesInSystem: Country[];
  bundlePrices: BundlePrice[];

  currentLocale: LocaleResponse;
  locales: LocaleResponse[];
  handleLocaleChange: (localeId: number) => void;

  addMode: boolean;
}

export interface Errors {
  name?: string;
  financeId?: string;
  mainProductVariants?: string;
  addonProductVariants?: { itemIdOrKey: number | string | undefined; error: string }[];
  description?: {
    description?: string;
    descriptionTranslation?: string;
  };
}

interface validateParams {
  name: string | undefined;
  financeId: string | undefined;
  description: string | undefined;
  descriptionTranslations: { localeId: number; translation: string }[] | undefined;
  mainItemOptionIds: number[] | undefined;
  items: AdminBundleItemWithKey[] | undefined;
  locales: LocaleResponse[];
  t: (T: string, params?: { languages?: string }) => string;
}

export const validate = (params: validateParams): { valid: boolean; errors: Errors } => {
  const { name, financeId, description, descriptionTranslations, mainItemOptionIds, items, locales, t } = params;
  const newErrors: Errors = {};

  // Name, finance Id
  if (name === '') {
    newErrors.name = t('editscreen.errors.name_blank');
  }
  if (financeId === '') {
    newErrors.financeId = t('editscreen.errors.finance_id_blank');
  }

  // Main item
  if (!mainItemOptionIds || mainItemOptionIds.length < 1) {
    newErrors.mainProductVariants = t('editscreen.errors.no_main_item_variants');
  }
  // Addon items
  const addonsErrors: { itemIdOrKey: number | string | undefined; error: string }[] = [];
  items?.forEach(item => {
    if (item.name === '') {
      addonsErrors.push({ itemIdOrKey: item.id || item.key, error: t('editscreen.errors.addon_item_name_blank') });
    } else if (item.options.length === 0) {
      addonsErrors.push({ itemIdOrKey: item.id || item.key, error: t('editscreen.errors.no_addon_item_variants') });
    }
  });
  if (addonsErrors.length !== 0) newErrors.addonProductVariants = addonsErrors;

  // Description
  const descriptionErrors: Errors['description'] = {};
  if (description === '') {
    descriptionErrors.description = t('editscreen.errors.description_blank');
  }
  // Description translation
  const descriptionBlankTranslations: string[] = [];
  locales.forEach(locale => {
    const translation = descriptionTranslations?.find(translation => translation.localeId === locale.id);
    if (!translation || !translation.translation) {
      descriptionBlankTranslations.push(locale.name);
    }
  });
  if (descriptionBlankTranslations.length !== 0) {
    descriptionErrors.descriptionTranslation = t('editscreen.errors.description_translation_blank', {
      languages: descriptionBlankTranslations.join(', '),
    });
  }
  if (!_.isEmpty(descriptionErrors)) newErrors.description = descriptionErrors;

  const valid = _.isEmpty(newErrors);
  return { valid, errors: newErrors };
};

export const ensureEditableItemInputChangesSavedOnSaveBtnClick = async (saveChangesBtnId: string) => {
  const element = document.getElementById(saveChangesBtnId);
  element?.focus();
  return new Promise(resolve => setTimeout(resolve, 0));
};

interface createBackendBundleParams {
  id: number | undefined;
  name: string | undefined;
  financeId: string | undefined;
  nameTranslations: { localeId: number; translation: string }[] | undefined;
  description: string | undefined;
  descriptionTranslations: { localeId: number; translation: string }[] | undefined;
  translationsMetadata: { id: number; localeId: number }[] | undefined;
  images: BundleImage[] | undefined;

  mainItemOptionIds: number[] | undefined;
  items: AdminBundleItem[] | undefined;
}

export const createBackendBundle = (params: createBackendBundleParams): AdminBundle => {
  const { id, name, financeId, nameTranslations, description, descriptionTranslations, translationsMetadata, images, mainItemOptionIds, items } =
    params;

  const translations = (translationsMetadata || [])
    // Create translation objects to be sent to backend
    // In case some field in translation object has only empty string -> do not send the empty string to backend
    .map(translationMetadata => {
      const name = nameTranslations?.find(t => t.localeId === translationMetadata.localeId && t.translation)?.translation;
      const description = descriptionTranslations?.find(t => t.localeId === translationMetadata.localeId && t.translation)?.translation;
      return {
        id: translationMetadata.id,
        locale_id: translationMetadata.localeId,
        name,
        description,
      };
    })
    // In case some translation has no content -> remove the whole translation
    // Currently each bundle translation should have description at least (validation checks this)
    // but in case this changes in future translation(s) with no content should be removed
    .filter(translation => {
      return translation.name || translation.description;
    });

  return {
    id: id || 0,
    name: name || '',
    finance_id: financeId || '',
    description: description || '',
    main_item_option_ids: mainItemOptionIds || [],
    items: items || [],
    images,
    translations,
    prices: [],
  };
};
